/* Section lifecycle adapter. Gallery, quantity, cart and checkout stay theme-owned. */
(() => {
  if (window.__mfPanditFigureGallery) {
    window.__mfPanditFigureGallery.mount(document);
    return;
  }

  const sections = new Map();

  const mountPurchaseAdapter = (section) => {
    const form = section.querySelector('.mf-source-product-form');
    const box = form?.querySelector('[data-kt-tiers]');
    const quantity = box?.querySelector('[data-kt-qtyval]');
    const button = form?.querySelector('[data-add-to-cart]');
    const previous = sections.get(section);
    // An editor/app refresh may preserve the section root but replace its form.
    // The adapter must follow the current nodes, not observers on the removed form.
    if (previous && previous.form === form && previous.box === box && previous.quantity === quantity
      && previous.button === button) {
      window.__mfKutirTiers?.(section);
      return;
    }
    previous?.dispose(previous.form !== form || previous.box !== box);
    // Re-use the existing controller; never replace its quantity or cart handlers.
    window.__mfKutirTiers?.(section);
    if (!form || !box || !quantity) return;
    // The shared AJAX controller reads this display as its submit-time backstop.
    // It protects against a third party changing the hidden input's value property.
    quantity.setAttribute('data-mf-object-quantity-value', '');
    const key = form.id.replace(/^mf-product-form-/, '');
    const findSticky = () => [...document.querySelectorAll('body > .mf-sticky-atc[data-kt-sticky-key]')]
      .find((element) => element.getAttribute('data-kt-sticky-key') === key);
    const sticky = findSticky();
    const parse = (name) => {
      try { return JSON.parse(box.getAttribute(name) || '{}'); } catch (_error) { return {}; }
    };
    const model = parse('data-kt-model');
    const prices = parse('data-kt-prices');
    const money = (cents) => {
      const digits = cents % 100 === 0 ? 0 : 2;
      return new Intl.NumberFormat('en-IN', {
        style: 'currency', currency: model.currency || 'INR', currencyDisplay: 'narrowSymbol',
        minimumFractionDigits: digits, maximumFractionDigits: digits,
      }).format(cents / 100).replace(/\u00a0/g, ' ');
    };
    const syncDisplay = () => {
      const qty = parseInt(quantity.textContent, 10);
      if (!Number.isFinite(qty) || qty < 1) return;
      const unit = Number(model.unitCents);
      const discount = qty >= 3 ? Number(model.off3) || 0 : qty >= 2 ? Number(model.off2) || 0 : 0;
      // The existing AJAX handler restores its saved button HTML after "Added".
      // If qty changes meanwhile, re-sync that restored price from the KT model.
      // This is display-only: no cart requests, offer changes or submit interception.
      const price = button?.querySelector('[data-mf-add-price]');
      if (price && Number.isFinite(Number(model.unitCents))) {
        const value = prices[qty] || money(Math.max(0, unit - discount) * qty);
        if (price.textContent !== value) price.textContent = value;
      }
      // KT moves the sticky outside its box, so its own descendant-only disabled
      // update no longer reaches these two buttons. Keep their affordance accurate.
      const minimum = Math.max(1, Number(model.min) || 1);
      const increment = Math.max(1, Number(model.increment) || 1);
      const maximum = Number(model.max) > 0 ? Number(model.max) : null;
      sticky?.querySelectorAll('[data-kt-sticky-step]').forEach((step) => {
        const next = qty + (Number(step.getAttribute('data-kt-sticky-step')) || 0) * increment;
        step.disabled = next < minimum || (maximum !== null && next > maximum);
      });
    };
    const displayObserver = new MutationObserver(syncDisplay);
    displayObserver.observe(quantity, { childList: true, subtree: true, characterData: true });
    if (button) displayObserver.observe(button, { childList: true, subtree: true });
    const visibilityObserver = sticky ? new MutationObserver(() => {
      sticky.inert = sticky.getAttribute('aria-hidden') === 'true';
    }) : null;
    if (sticky) {
      sticky.inert = sticky.getAttribute('aria-hidden') === 'true';
      visibilityObserver.observe(sticky, { attributes: true, attributeFilter: ['aria-hidden'] });
    }
    const dispose = (removeSticky = true) => {
      displayObserver.disconnect();
      visibilityObserver?.disconnect();
      // The shared controller deliberately portals its sticky to document.body.
      // Remove only this section's portal when its form leaves the theme editor.
      // A changed CTA node in the same native form needs new observers,
      // but its still-current native sticky must remain mounted.
      if (removeSticky && key.startsWith('pandit-figure-mobile-')) sticky?.remove();
      sections.delete(section);
    };
    sections.set(section, { form, box, quantity, button, dispose });
    syncDisplay();
  };

  const mount = (root) => {
    [...sections.entries()].forEach(([section, state]) => { if (!section.isConnected) state.dispose(); });
    if (root.matches?.('[data-pandit-figure]')) mountPurchaseAdapter(root);
    root.querySelectorAll('[data-pandit-figure]').forEach(mountPurchaseAdapter);
    // Public, idempotent hook from mifoa-gallery-swipe.js. The common theme
    // owns thumbnail activation; this hook only attaches its shared swipe layer.
    window.__mfGallerySwipeInit?.();
  };

  window.__mfPanditFigureGallery = { mount };
  document.addEventListener('shopify:section:load', (event) => mount(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    [...sections.entries()].forEach(([section, state]) => {
      if (event.target === section || event.target.contains(section)) state.dispose();
    });
  });
  // Theme Editor block selection uses the same thumbnail path as the storefront.
  // No customer-facing gallery click/swipe/keyboard handlers are duplicated here.
  document.addEventListener('shopify:block:select', (event) => {
    const slide = event.target.closest?.('[data-gallery-main]');
    const section = slide?.closest('[data-pandit-figure]');
    if (!section) return;
    const index = slide.getAttribute('data-gallery-main');
    section.querySelector(`[data-gallery-thumb="${index}"]`)?.click();
  });
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => mount(document), { once: true });
  } else {
    mount(document);
  }
})();
